const { SecretClient } = require('@azure/keyvault-secrets');
const { DefaultAzureCredential, ClientSecretCredential } = require('@azure/identity');
const config = require('./config');
const { client_cache_timeout_in_seconds, keyvault_service_client_secret, keyvault_service_tenant_id, keyvault_service_client_id, use_identity } = require('./config');
const cache = require('node-cache');
let clientCache = new cache({stdTTL: client_cache_timeout_in_seconds || 3600, useClones: false}); //clones are turned off for clientCache as SecretClient opens a TCP connection which cannot be cloned

/**
 * Gets the credential for the given use case. If you are using an azure Identity,
 * this function will return the DefaultAzureCredential, if you are using a client
 * id, secret and tenant Id then you will receive the ClientSecretCredential.
 * @returns Credential
 * @throws error: returns an error if you have misconfigured your application with
 * detailed messages.
 */
const getCredentialForVault = () => {
    if(use_identity === '1' || use_identity === 'true') {
        return new DefaultAzureCredential();
    }
    if(use_identity !== '1' || use_identity !== 'true') {
        if(!keyvault_service_client_id || !keyvault_service_client_secret || !keyvault_service_tenant_id) {
            throw `The keyvault_service_client_id, keyvault_service_client_secret and keyvault_service_tenant_id are required when using a client secret credential.`;
        }
        return new ClientSecretCredential(
            config.keyvault_service_tenant_id,
            config.keyvault_service_client_id,
            config.keyvault_service_client_secret
        );
    }
    throw `The given configuration is invalid. You must either provide a keyvault_service_client_id, keyvault_service_client_secret and keyvault_service_tenant_id or use an identity for this service. Please review documentation.`
}

/**
 * Creates a client per keyvault with a timeout set in the client_cache_timeout_in_seconds configuration.
 * 
 * @param {string} keyVault The keyVault name where the value is stored
 * @returns SecretClient(keyVault, DefaultAzureCredential()) || SecretClient(keyVault, ClientSecretCredential)
 */
const getClient = (keyVault) => {
    const cacheValue = clientCache.get(keyVault);
    if (!clientCache || !cacheValue || !cacheValue) {
        let credential = getCredentialForVault();
        console.debug("Secret client not found in cache, creating a new client.");
        const client = new SecretClient(keyVault, credential);
        clientCache.set(
            keyVault, 
            client
        );
    }
    
    return clientCache.get(keyVault);
}

/**
 * Gets the given secretName from a given keyvault using the DefaultAzureCredential()
 * @param {string} keyVault The name of the key vault where the secret resides.
 * @param {string} secretName The name of the secret you are trying to retrieve
 * @returns string: secret value.
 */
const getSecret = async (keyVault, secretName) => {
    const client = getClient(keyVault);
    try {
        const secret = await client.getSecret(secretName);
        return secret.value;
    } catch (err) {
        if (err.statusCode && err.statusCode === 401) {
            throw `The credential provided does not have access to the key vault provided ${keyVault}. Please confirm that you have access to the keyvault and that you are logged into the correct tenancy. ${err}`;
        }
        if (err.statusCode && err.statusCode === 403) {
            throw `The firewall policy has blocked your request to ${keyVault}. Please review and update the firewall rules to include your current IP. ${err}`;
        }
        if (err.statusCode && err.statusCode === 429) {
            throw `The keyvault responded with a 429 and is currently being throttled. Please retry this request later.`
        }
        throw err;
    }
};

/**
 * Sets the given secret in the keyvault.
 * @param {url} keyVault url for the keyvault to set the secret in.
 * @param {string} secretName secret name
 * @param {string} secretValue secret value
 */
const setSecret = async (keyVault, secretName, secretValue) => {
    const client = getClient(keyVault);
    try {
        return await client.setSecret(secretName, secretValue);
    } catch (err) {
        if (err.statusCode && err.statusCode === 401) {
            throw `The credential provided does not have access to the key vault provided ${keyVault}. Please confirm that you have access to the keyvault and that you are logged into the correct tenancy.\r\n${err}`;
        }
        if (err.statusCode && err.statusCode === 403) {
            throw `The firewall policy has blocked your request to ${keyVault}. Please review and update the firewall rules to include your current IP. \r\n${err}`;
        }
        if (err.statusCode && err.statusCode === 429) {
            throw `The keyvault responded with a 429 and is currently being throttled. Please retry this request later.`
        }
        throw err;
    }
}

module.exports = { getSecret, setSecret };